import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { AGWorkerService } from './shared/services/workers/agworker.service';
import { SocketService } from './shared/services/client/socket.service';
import { DeviceService } from './shared/services/client/device.service';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'CyberRoom Admin';
  mobileQuery: MediaQueryList;
  isMobileNavOpen = false;
  // Parent page info (now reactive)
  parentUrl$ = new BehaviorSubject<string | null>(null);
  parentOrigin$ = new BehaviorSubject<string | null>(null);

  private subscriptions = new Subscription();
  welcomeMessage = '';
  private _mobileQueryListener: () => void;
  private _messageListener: (event: MessageEvent) => void;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher,
    private router: Router,
    private agWorker: AGWorkerService,
    private socketService: SocketService,
    private deviceService:DeviceService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 768px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();

    // For modern browsers
    if (this.mobileQuery.addEventListener) {
      this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    } else {
      // For older browsers
      this.mobileQuery.addListener(this._mobileQueryListener);
    }

    this._messageListener = (event: MessageEvent) => this.handleMessage(event);
  }

  ngOnInit(): void {
    window.addEventListener('message', this._messageListener);
    this.setupParentUrlDetection();
    this.setupSocketParentUrlSync();
  }

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      return true; // safer default for cross-origin
    }
  }
  private sendParentInfoToBackend(url: string, origin: string): void {
    this.socketService.socket.emit('parent-page-info', {
      parentUrl: url,
      parentOrigin: origin,
      timestamp: Date.now(),
      socketId: this.socketService.socketId$.value,
    });
  }
  private setParentInfo(url: string, origin: string): void {
    this.parentUrl$.next(url);
    this.parentOrigin$.next(origin);

    console.log('Parent URL set:', url);
    this.deviceService.oSuccessNotification(
      'Success',
      `Connected to parent: ${new URL(url).hostname}`
    );

    // Immediate send if socket is ready
    if (this.socketService.isConnected) {
      this.sendParentInfoToBackend(url, origin);
    }
  }
  private setupParentUrlDetection(): void {
    if (this.isInIframe()) {
      // 1. Try direct same-origin access
      try {
        const url = window.parent.location.href;
        const origin = window.parent.location.origin;
        this.setParentInfo(url, origin);
        return;
      } catch (e) {
        console.info('Cross-origin iframe – using fallbacks');
      }

      // 2. Fallback to referrer
      if (document.referrer) {
        try {
          const referrer = new URL(document.referrer);
          this.setParentInfo(referrer.href, referrer.origin);
          return;
        } catch (e) {
          console.warn('Invalid referrer URL:', document.referrer);
        }
      }

      // 3. Request via postMessage (cross-origin)
      this.deviceService.oInfoNotification('Info', 'Requesting parent page URL...');
      window.parent.postMessage({ type: 'REQUEST_PARENT_URL' }, '*');

      window.addEventListener('message', this._messageListener);

      // Timeout fallback
      setTimeout(() => {
        if (!this.parentUrl$.value) {
          this.deviceService.oErrorNotification(
            'Warning',
            'Parent page did not respond with URL after 10s'
          );
        }
      }, 10000);
    } else {
      console.info('App not running in iframe – parent detection skipped');
    }
  }
  

  private setupSocketParentUrlSync(): void {
    // Send parent info whenever socket connects (handles reconnects)
    this.subscriptions.add(
      this.socketService.isConnected$.subscribe((connected) => {
        if (connected && this.parentUrl$.value && this.parentOrigin$.value) {
          this.sendParentInfoToBackend(this.parentUrl$.value, this.parentOrigin$.value);
        }
      })
    );
  }


  // Add the missing method that's called from template
  closeMobileNav(): void {
    this.isMobileNavOpen = false;
    this.updateBodyScroll(false);
    this.changeDetectorRef.detectChanges();
  }
  async loadWelcome() {
    try {
      this.welcomeMessage = await this.agWorker.welcome();
      // Try typing, scrolling, opening dev tools → everything stays SMOOTH!
    } catch (err) {
      this.welcomeMessage = 'Failed to connect to AG Network';
    }
  }


  toggleMobileNav(): void {
    this.isMobileNavOpen = !this.isMobileNavOpen;
    this.updateBodyScroll(this.isMobileNavOpen);
    this.changeDetectorRef.detectChanges();
  }

  openMobileNav(): void {
    if (!this.isMobileNavOpen) {
      this.isMobileNavOpen = true;
      this.updateBodyScroll(true);
      this.changeDetectorRef.detectChanges();
    }
  }

  private updateBodyScroll(lock: boolean): void {
    if (lock) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }

  // Handle incoming postMessage from parent
  private handleMessage(event: MessageEvent): void {
    if (!event.data || typeof event.data !== 'object') {
      return;
    }

    // Handle specific message types
    if (event.data.type === 'PARENT_URL') {
      // Handle parent URL logic here
      console.log('Parent URL received:', event.data.url);
    }
  }

  ngOnDestroy(): void {
    // Remove media query listener
    if (this.mobileQuery.removeEventListener) {
      this.mobileQuery.removeEventListener('change', this._mobileQueryListener);
    } else {
      this.mobileQuery.removeListener(this._mobileQueryListener);
    }

    // Remove message listener
    window.removeEventListener('message', this._messageListener);

    // Reset body scroll
    this.updateBodyScroll(false);
  }
}
